/*
 * Universal pickup-availability engine.
 *
 * Three layers (see .claude/features/pickup-availability/OUTPUT-implementation-plan.md):
 *   1. RESOLVER  — per product shape; returns the variant IDs the current
 *                  selection involves (the things being bought & carried out).
 *   2. ENGINE    — fetches each variant's store availability as JSON, keeps only
 *                  locations where EVERY relevant variant is pickup-enabled AND
 *                  available (strict), renders pill + drawer.
 *   3. TRIGGERS  — re-run on variant-change / any selection click (debounced).
 *
 * Config + initial state are injected by snippets/pickup-availability-block.liquid
 * via a <script type="application/json" data-pickup-config> tag. The engine owns
 * all markup so it stays locale-safe (translation strings come from the config).
 *
 * Debug: append ?pickup_debug=1 to the URL to log the resolved variant set and
 * the per-component reason a location was disqualified.
 */
(function () {
  if (window.__pickupEngineLoaded) return;
  window.__pickupEngineLoaded = true;

  var configEl = document.querySelector('[data-pickup-config]');
  if (!configEl) return;

  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var DEBUG = /[?&]pickup_debug=1\b/.test(window.location.search);
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[pickup]'].concat([].slice.call(arguments)));
  }

  var ROOT = CONFIG.rootUrl || '/';
  if (ROOT.charAt(ROOT.length - 1) !== '/') ROOT += '/';

  var I18N = CONFIG.i18n || {};
  var ICONS = CONFIG.icons || {};

  // Per-session cache of variantId -> availability array. Stock is effectively
  // static during a browsing session; this collapses repeated toggles to zero
  // network cost.
  var cache = {};
  var inflight = null;
  var lastSignature = null;
  var DRAWER_ID = 'Pickup-Availability';

  /* ─── helpers ─────────────────────────────────────────────────────────── */

  function wrapper() {
    return document.querySelector('.pickup-availability-wrapper');
  }
  function drawer() {
    return document.getElementById(DRAWER_ID);
  }
  function uniq(arr) {
    return arr.filter(function (v, i) { return v && arr.indexOf(v) === i; });
  }

  /* ─── Layer 1: resolvers ──────────────────────────────────────────────── */

  function mainVariantId() {
    var form = document.querySelector('.Bundle1_product_form');
    var input = form && form.querySelector('input[name="id"]:not(:disabled)');
    if (input && input.value) return String(input.value);
    var w = wrapper();
    return w && w.dataset.variantId ? String(w.dataset.variantId) : null;
  }

  // The leg SKU to validate = the REAL leg added to cart for the selected
  // colour. The double-SKU customizer writes it to #frame1_id_input; the legacy
  // single-frame path uses #frame_legacy_id_input. These are the products whose
  // warehouse stock is actually managed.
  //
  // NOTE: the swatch's data-id (the table_frame product) is only a display
  // representative — it can carry its own, separate inventory that does NOT
  // reflect the real leg's warehouse stock — so it is used only as a last
  // resort when the hidden inputs aren't present.
  function selectedLegId() {
    var f1 = document.getElementById('frame1_id_input');
    if (f1 && !f1.disabled && f1.value) return String(f1.value);
    var legacy = document.getElementById('frame_legacy_id_input');
    if (legacy && !legacy.disabled && legacy.value) return String(legacy.value);
    var els = [];
    document.querySelectorAll('.frame-option.active, .frame-option-mobile.active').forEach(function (e) { els.push(e); });
    if (!els.length) return null;
    var visible = els.filter(function (e) { return e.offsetParent !== null; })[0];
    var el = visible || els[0];
    return el && el.dataset.id ? String(el.dataset.id) : null;
  }

  function selectedCableIds() {
    var ids = [];
    ['selectedCableManagementId', 'selectedCableManagementIdMobile'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && !el.disabled && el.value && el.value !== 'none') ids.push(String(el.value));
    });
    return ids;
  }

  function selectedAddonIds() {
    var ids = [];
    (window.selectedAddons || []).forEach(function (a) { if (a && a.id) ids.push(String(a.id)); });
    (window.selectedBundles || []).forEach(function (b) { if (b && b.id) ids.push(String(b.id)); });
    return ids;
  }

  var RESOLVERS = {
    'double-sku-desk': function () {
      return uniq([mainVariantId(), selectedLegId()]
        .concat(selectedCableIds())
        .concat(selectedAddonIds()));
    },
    'default': function (evt) {
      return uniq([(evt && evt.variantId) || mainVariantId()]);
    }
  };

  // Determine the product shape from the DOM, not from CONFIG.pickupType.
  // Rationale: btx-main-product.liquid contains a `{% for product in
  // collections.all.products %}` loop (a "TEST PRODUCT" hack) that clobbers the
  // Liquid `product` variable BEFORE this snippet is rendered, so the snippet
  // can be handed the wrong product and emit pickupType:"default" for a desk.
  // The presence of frame swatches is the definitive signal that the leg SKU
  // must be part of the pickup check.
  function effectiveType() {
    if (document.querySelector('.frame-option, .frame-option-mobile')) return 'double-sku-desk';
    return CONFIG.pickupType || 'default';
  }

  function resolveIds(evt) {
    var fn = RESOLVERS[effectiveType()] || RESOLVERS['default'];
    return fn(evt);
  }

  /* ─── Layer 2: fetch + intersect + render ─────────────────────────────── */

  function fetchAvailability(variantId, signal) {
    if (cache[variantId]) return Promise.resolve(cache[variantId]);
    var url = ROOT + 'variants/' + variantId + '/?section_id=pickup-availability-data';
    return fetch(url, { signal: signal })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        // The Section Rendering API wraps output in a .shopify-section element.
        // Read the text content, then slice between the outer [ ] so neither the
        // wrapper nor stray whitespace can break JSON.parse.
        var raw = new DOMParser().parseFromString(text, 'text/html').body.textContent;
        var start = raw.indexOf('[');
        var end = raw.lastIndexOf(']');
        var data = start === -1 || end === -1 ? [] : JSON.parse(raw.slice(start, end + 1));
        cache[variantId] = data;
        return data;
      });
  }

  // location entry that is genuinely pickup-able for this variant
  function okAt(availList, locationId) {
    for (var i = 0; i < availList.length; i++) {
      if (String(availList[i].locationId) === String(locationId)) {
        return availList[i].pickUpEnabled === true && availList[i].available === true;
      }
    }
    return false; // no record at this location => not pickup-able (strict)
  }

  // Returns { qualifying: {locId: entry}, ordered: [entry...] } anchored on the
  // first variant (the tabletop / selected variant), strict across all variants.
  function intersect(idsData) {
    var anchor = idsData[0].data; // tabletop / primary variant availability
    var qualifying = {};
    var ordered = [];
    anchor.forEach(function (entry) {
      var locId = entry.locationId;
      if (!(entry.pickUpEnabled === true)) return; // anchor must offer pickup here
      var everyOk = idsData.every(function (d) { return okAt(d.data, locId); });
      if (everyOk) {
        qualifying[locId] = entry;
      }
      // keep anchor order for the drawer (qualifying or not, if anchor pickup-enabled)
      ordered.push({ entry: entry, qualifies: everyOk && entry.pickUpEnabled === true });
    });
    if (DEBUG) {
      anchor.forEach(function (entry) {
        idsData.forEach(function (d) {
          if (!okAt(d.data, entry.locationId)) {
            log('location "' + entry.name + '" disqualified by variant ' + d.id);
          }
        });
      });
    }
    return { qualifying: qualifying, ordered: ordered };
  }

  function pillHtml(entry) {
    var title = (I18N.availableAt || 'Pick up available at __LOCATION__').replace('__LOCATION__', entry.name);
    return '' +
      '<div class="pickup-availability-information">' +
        (ICONS.check || '') +
        '<div class="pickup-availability-information-container">' +
          '<p class="pickup-availability-information__title">' + title + '</p>' +
          '<p class="pickup-availability-information__stock">' + (entry.pickUpTime || '') + '</p>' +
          '<button type="button" class="pickup-availability-information__button text-button">' +
            (I18N.viewStoreInfo || 'View store information') +
          '</button>' +
        '</div>' +
      '</div>';
  }

  function drawerHtml(ordered) {
    var items = ordered.map(function (row) {
      var e = row.entry;
      var stock = row.qualifies
        ? (ICONS.check || '') + ' ' + (I18N.available || 'Pickup available') + ', ' + (e.pickUpTime || '').toLowerCase()
        : (ICONS.cross || '') + ' ' + (I18N.unavailableAt || 'Pickup currently unavailable at __LOCATION__').replace('__LOCATION__', e.name);
      var phone = e.phone ? '<p class="pickup-availability-list__phone pickup-availability-small-text">' + e.phone + '<br></p>' : '';
      return '' +
        '<li class="pickup-availability-list__item">' +
          '<h6 class="pickup-availability-list__location body-font">' + e.name + '</h6>' +
          '<div class="pickup-availability-list__stock">' + stock + '</div>' +
          '<address class="pickup-availability-list__address">' + (e.address || '') + '</address>' +
          phone +
        '</li>';
    }).join('');
    return '' +
      '<div class="side-panel-inner">' +
        '<div class="side-panel-header"><div>' +
          '<span class="h4">' + (I18N.heading || 'Store availability') + '</span>' +
          '<side-panel-close class="side-panel-close">' + (I18N.close || 'Close') + '</side-panel-close>' +
        '</div></div>' +
        '<div class="side-panel-content">' +
          '<ul class="pickup-availability-list" role="list">' + items + '</ul>' +
        '</div>' +
      '</div>';
  }

  function render(result) {
    var w = wrapper();
    if (!w) return;
    var d = drawer();

    // closest qualifying location, in anchor order
    var closest = null;
    for (var i = 0; i < result.ordered.length; i++) {
      if (result.ordered[i].qualifies) { closest = result.ordered[i].entry; break; }
    }

    if (!closest) {
      w.innerHTML = '';
      w.removeAttribute('available');
      if (d) d.innerHTML = '';
      log('no qualifying location → badge hidden');
      return;
    }

    w.innerHTML = pillHtml(closest);
    w.setAttribute('available', '');
    if (d) d.innerHTML = drawerHtml(result.ordered);
    log('available at "' + closest.name + '"');
  }

  function run(evt) {
    var w = wrapper();
    if (!w) return Promise.resolve();

    var ids = resolveIds(evt);
    if (!ids.length) return Promise.resolve();

    var signature = ids.join(',');
    if (signature === lastSignature && !(evt && evt.force)) return Promise.resolve();
    lastSignature = signature;
    log('resolved variant set:', ids);

    if (inflight) inflight.abort();
    inflight = new AbortController();
    var signal = inflight.signal;

    return Promise.all(ids.map(function (id) {
      return fetchAvailability(id, signal).then(function (data) { return { id: id, data: data }; });
    }))
      .then(function (idsData) { render(intersect(idsData)); })
      .catch(function (e) {
        if (e && e.name === 'AbortError') return;
        // Conservative on error: hide rather than show a possibly-wrong badge.
        var w2 = wrapper();
        if (w2) { w2.innerHTML = ''; w2.removeAttribute('available'); }
        log('error → badge hidden', e);
      });
  }

  /* ─── Layer 3: triggers ───────────────────────────────────────────────── */

  var debounceTimer = null;
  function schedule(evt) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { run(evt); }, 150);
  }

  // Tabletop colour/size/edge (and accessory variant pickers) dispatch this.
  document.addEventListener('product:variant-change', function (e) {
    var v = e.detail && e.detail.variant;
    if (v && v.id) {
      // Keep the fallback id fresh so a later click-triggered run (which has no
      // event payload) still resolves the current variant in 'default' mode.
      var w = wrapper();
      if (w) w.dataset.variantId = String(v.id);
    }
    schedule(v ? { variantId: String(v.id) } : {});
  });

  // Any selection click (leg swatch, cable, addon, etc.). The signature guard in
  // run() means an unchanged set costs nothing, so we can trigger generously
  // without knowing every customizer's selector.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.pickup-availability-information__button')) {
      // Drawer toggle — not a selection change.
      var d = drawer();
      if (d) d.classList.toggle('active');
      document.body.classList.toggle('open-cc');
      return;
    }
    schedule({});
  });

  // Initial hydrate: reconcile the SSR pill with the live selection and populate
  // the drawer (SSR renders only the pill). One fetch on load.
  function init() { run({ force: true }); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 0); });
  } else {
    setTimeout(init, 0);
  }
})();
